import React, { useState, useCallback, useEffect } from 'react';
import { Combobox, InputBase, useCombobox, Text, Group, Anchor } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { useFunds } from 'src/company/hooks/useFunds';
import { Fund } from 'src/company/types';
import { AddFundModal } from 'src/company/funds/components/AddFundModal';

interface FundCodeItem {
  value: string;
  label: string;
  name?: string;
}

const transformFundsToFundCodeItems = (funds: Fund[]): FundCodeItem[] => {
  return funds.map(fund => ({
    value: fund.id,
    label: `${fund.code} - ${fund.name}`,
    name: fund.name,
  }));
};

interface FundCodeDropdownProps {
  form: UseFormReturnType<FormValuesType>;
  formFieldName: string;
  fieldName: string;
  disabled?: boolean;
  onChange?: (value: unknown, fieldName: string) => void;
  maxResults?: number;
  editMode?: boolean;
}

export const FundCodeDropdown: React.FC<FundCodeDropdownProps> = ({
  form,
  formFieldName,
  fieldName,
  disabled = false,
  onChange,
  maxResults = 3,
  editMode = true,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { funds, refetch } = useFunds();
  const [data, setData] = useState<FundCodeItem[]>([]);
  const [search, setSearch] = useState('');
  const [modalOpened, setModalOpened] = useState(false);

  useEffect(() => {
    if (funds && funds.length > 0) {
      setData(transformFundsToFundCodeItems(funds));
    }
  }, [funds]);

  const inputProps = form.getInputProps(formFieldName);
  const formValue = inputProps.value ?? inputProps.defaultValue;

  const exactOptionMatch = data.some(item => item.value === search);
  const filteredOptions = exactOptionMatch
    ? data.slice(0, maxResults)
    : data
        .filter(
          item =>
            item.label.toLowerCase().includes(search.toLowerCase().trim()) ||
            item.value.toLowerCase().includes(search.toLowerCase().trim()),
        )
        .slice(0, maxResults);

  const selectedItem = formValue ? data.find(item => item.value === formValue) : null;
  const displayValue = selectedItem?.label || '';

  useEffect(() => {
    if (formValue) {
      const item = data.find(i => i.value === formValue);
      if (item) setSearch(item.label);
    } else {
      setSearch('');
    }
  }, [formValue, data]);

  const handleValueChange = useCallback(
    (value: string | null) => {
      if (onChange) {
        onChange(value, fieldName);
      } else {
        inputProps.onChange(value);
      }

      if (value) {
        const newSelectedItem = data.find(item => item.value === value);
        if (newSelectedItem) {
          setSearch(newSelectedItem.label);
        }
      } else {
        setSearch('');
      }
    },
    [onChange, fieldName, inputProps, data],
  );

  const handleModalClose = useCallback(async () => {
    setModalOpened(false);
    await refetch();
  }, [refetch]);

  if (!editMode) {
    const selectedFund = formValue ? funds.find(fund => fund.id === formValue) : null;
    if (selectedFund && displayValue) {
      return (
        <Anchor
          href={`/company/funds/${selectedFund.id}`}
          target="_blank"
          c="blue.7"
          td="none"
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {displayValue}
          <IconExternalLink size={12} style={{ opacity: 0.7 }} />
        </Anchor>
      );
    }
    return <Text>{displayValue || ''}</Text>;
  }

  return (
    <>
      <Combobox
        store={combobox}
        onOptionSubmit={val => {
          if (val === '$create') {
            setModalOpened(true);
          } else {
            handleValueChange(val);
          }
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            rightSection={<Combobox.Chevron />}
            value={search}
            onChange={event => {
              combobox.openDropdown();
              combobox.updateSelectedOptionIndex();
              setSearch(event.currentTarget.value);
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown();
              setSearch(displayValue);
            }}
            placeholder="Search for Fund code..."
            rightSectionPointerEvents="none"
            disabled={disabled}
            error={inputProps.error}
          />
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {filteredOptions.map(item => (
              <Combobox.Option value={item.value} key={item.value}>
                {item.label}
              </Combobox.Option>
            ))}
            {!exactOptionMatch && (
              <Combobox.Option value="$create">
                <Group gap="xs">
                  <Text size="sm">+ Create new fund</Text>
                  {search.trim().length > 0 && (
                    <Text size="xs" c="dimmed">
                      ({search})
                    </Text>
                  )}
                </Group>
              </Combobox.Option>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>

      <AddFundModal opened={modalOpened} onClose={handleModalClose} />
    </>
  );
};
